/**
 * Session — the active meditation screen.
 *
 * Route: `/quiet-hours/:category/:duration?guided=<0|1>`
 *
 * The orb, the voice, and the music bed are a single experience. We
 * achieve that by running both audio elements (voice + bed) and a
 * single `audio.currentTime`-driven clock that feeds the orb's breath
 * cycle. Pause pauses everything; End fades everything; the orb's
 * scale tracks the voice's actual position into its file (not a free
 * CSS animation).
 *
 * Layers (back to front):
 *  - AmbientField — soft per-category atmosphere image.
 *  - BreathingVisual — the luminous orb, driven by the voice clock
 *    (or by `performance.now()` when unguided).
 *  - Time pill (top-right) and Pause / End controls (bottom).
 *
 * Lifecycle:
 *  1. On mount: load cue file (if guided), start music bed, start voice.
 *  2. Tick: a 250ms timer updates remaining seconds. When the session
 *     length is reached, fade to the "Thank you for the time." card.
 *  3. Pause: pauses both audio elements. The orb freezes because its
 *     time source is the (paused) voice element. Unguided sessions
 *     pause the music; the orb's free clock is also frozen via the
 *     `paused` flag.
 *  4. End: fade music + voice, hold the thank-you card ~5s, navigate
 *     back to the hub.
 */
import { AnimatePresence, motion } from 'motion/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { usePreferences } from '@/preferences';

import { AmbientField } from './AmbientField';
import { BreathingVisual } from './BreathingVisual';
import {
  getCategory,
  isDurationMinutes,
  type DurationMinutes,
} from './categories';
import { FADE } from './motion';
import { playMusic, type MusicPlayback } from './music';
import { paletteFor } from './palette';
import { startAmbient, stopAmbient } from './sound';
import {
  loadVoiceCues,
  playVoice,
  type VoiceCueFile,
  type VoicePlayback,
} from './voice';

import './Session.css';

type SessionPhase = 'running' | 'paused' | 'closing';

export function QuietHoursSession() {
  const { category: slug, duration: durationRaw } = useParams<{
    category: string;
    duration: string;
  }>();
  const [search] = useSearchParams();
  const { theme } = usePreferences();
  const navigate = useNavigate();

  const category = useMemo(() => (slug ? getCategory(slug) : undefined), [slug]);
  const duration = useMemo<DurationMinutes | undefined>(() => {
    if (!durationRaw) return undefined;
    const n = Number.parseInt(durationRaw, 10);
    return isDurationMinutes(n) ? n : undefined;
  }, [durationRaw]);
  const guided = search.get('guided') === '1';

  const [phase, setPhase] = useState<SessionPhase>('running');
  const [remainingSec, setRemainingSec] = useState<number>(
    duration ? duration * 60 : 0,
  );
  const [cues, setCues] = useState<VoiceCueFile | null>(null);
  const [activeLineText, setActiveLineText] = useState<string | null>(null);

  const voiceRef = useRef<VoicePlayback | null>(null);
  const musicRef = useRef<MusicPlayback | null>(null);
  const phaseRef = useRef<SessionPhase>('running');
  const orbClockOffsetRef = useRef(0); // accumulated elapsed seconds for unguided/fallback
  const orbClockResumeAtRef = useRef(performance.now());

  // Keep a ref to the current phase so the orb time source can read it
  // without re-subscribing each phase change.
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // Begin: load cues, start music + voice.
  //
  // React 19 + StrictMode runs this effect twice on mount in dev (mount
  // → cleanup → re-mount). We can't gate with a ref to "run only once"
  // because then the StrictMode tear-down stops the audio for good.
  // Instead, do tear-down + re-init every run — `playVoice` and
  // `playMusic` are cheap and idempotent.
  useEffect(() => {
    if (!category || !duration) return;

    let cancelled = false;
    musicRef.current = playMusic(category.slug, duration);

    if (guided) {
      void loadVoiceCues(category.slug, duration).then((c) => {
        if (cancelled || !c) return;
        setCues(c);
      });
      voiceRef.current = playVoice(category.slug, duration);
    }

    // Fallback: if no music bed file exists (asset hasn't been
    // generated yet), the synthesized ambient drone still gives the
    // room a presence.
    if (!musicRef.current) {
      startAmbient(category.slug);
    }

    return () => {
      cancelled = true;
      musicRef.current?.stop();
      musicRef.current = null;
      voiceRef.current?.stop();
      voiceRef.current = null;
      stopAmbient();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tick the remaining clock (250ms — smoother than 1s for the pause
  // label transitions, and the cue-driven orb already runs at rAF rate).
  useEffect(() => {
    if (phase !== 'running') return;
    const tStart = performance.now();
    const remainingAtStart = remainingSec;
    const id = window.setInterval(() => {
      const elapsed = (performance.now() - tStart) / 1000;
      const left = Math.max(0, remainingAtStart - elapsed);
      setRemainingSec(left);
      if (left <= 0) {
        window.clearInterval(id);
        setPhase('closing');
      }
    }, 250);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Update the active line label (subtitle) from cues + voice currentTime.
  useEffect(() => {
    if (!cues || !voiceRef.current) return;
    const el = voiceRef.current.el;
    let raf = 0;
    const tick = () => {
      const t = el.currentTime;
      const line = findActiveLine(cues, t);
      setActiveLineText(line?.text ?? null);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [cues]);

  // When closing: fade out and navigate back to the hub.
  useEffect(() => {
    if (phase !== 'closing') return;
    musicRef.current?.stop();
    voiceRef.current?.stop();
    stopAmbient();
    const id = window.setTimeout(() => {
      navigate('/quiet-hours');
    }, 5500);
    return () => window.clearTimeout(id);
  }, [phase, navigate]);

  // Pause / resume audio elements when phase changes.
  useEffect(() => {
    if (phase === 'paused') {
      voiceRef.current?.pause();
      musicRef.current?.pause();
      // Snapshot the free-clock elapsed time at the moment of pause so
      // unguided sessions don't jump forward when resumed.
      orbClockOffsetRef.current +=
        (performance.now() - orbClockResumeAtRef.current) / 1000;
    } else if (phase === 'running') {
      voiceRef.current?.resume();
      musicRef.current?.resume();
      orbClockResumeAtRef.current = performance.now();
    }
  }, [phase]);

  const endNow = useCallback(() => {
    setPhase('closing');
  }, []);

  const togglePause = useCallback(() => {
    setPhase((p) => (p === 'running' ? 'paused' : p === 'paused' ? 'running' : p));
  }, []);

  // Orb time source — voice currentTime when guided and voice exists,
  // otherwise a free clock that respects pause.
  const getOrbTime = useCallback((): number => {
    const v = voiceRef.current?.el;
    if (v && !Number.isNaN(v.currentTime) && v.currentTime > 0) {
      return v.currentTime;
    }
    // Free clock: accumulated offset + current-run elapsed (only when
    // running; when paused, the elapsed delta is frozen at pause time).
    const live =
      phaseRef.current === 'running'
        ? (performance.now() - orbClockResumeAtRef.current) / 1000
        : 0;
    return orbClockOffsetRef.current + live;
  }, []);

  if (!category || !duration) {
    return (
      <div className="qh-session qh-session--missing" data-theme={theme}>
        <p>That space isn&apos;t available.</p>
      </div>
    );
  }

  const atmosphere =
    theme === 'dark' ? category.atmosphereDark : category.atmosphereLight;
  const pal = paletteFor(theme);
  const orbColor =
    theme === 'dark' ? category.orbColorDark : category.orbColorLight;
  const haloColor =
    theme === 'dark' ? category.haloColorDark : category.haloColorLight;

  return (
    <div
      className="qh-session"
      data-theme={theme}
      data-phase={phase}
      data-category={category.slug}
    >
      <AmbientField src={atmosphere} vignetteColor={pal.paper} />

      <AnimatePresence mode="wait">
        {phase !== 'closing' ? (
          <motion.div
            key="running"
            className="qh-session__stage"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={FADE.slow}
          >
            <BreathingVisual
              pattern={category.breath}
              orbColor={orbColor}
              haloColor={haloColor}
              getElapsedSec={getOrbTime}
              showLabel={!activeLineText}
            />

            {activeLineText && (
              <p
                className="qh-session__subtitle"
                data-testid="qh-subtitle"
                aria-live="polite"
              >
                {activeLineText}
              </p>
            )}

            <div className="qh-session__time" aria-live="polite">
              {formatRemaining(remainingSec)}
            </div>

            <div className="qh-session__controls">
              <button
                type="button"
                className="qh-session__pause"
                onClick={togglePause}
                aria-label={phase === 'paused' ? 'Resume the session' : 'Pause the session'}
              >
                {phase === 'paused' ? 'Resume' : 'Pause'}
              </button>
              <button
                type="button"
                className="qh-session__end"
                onClick={endNow}
                aria-label="End the session"
              >
                End session
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="closing"
            className="qh-session__closing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={FADE.slow}
          >
            <motion.p
              className="qh-session__thanks"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 2.2, ease: [0.4, 0, 0.2, 1] }}
            >
              Thank you for the time.
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Find the cue line currently being spoken at time `t` (seconds into
 * the voice MP3). Returns `undefined` during silence. Exported for tests.
 */
export function findActiveLine(
  cues: VoiceCueFile,
  t: number,
): VoiceCueFile['lines'][number] | undefined {
  // Cue lines are written in order — short linear scan is fine for our
  // 4–16 line scripts; no need for binary search.
  for (const line of cues.lines) {
    if (t >= line.startSec && t <= line.endSec + 0.05) return line;
    if (line.startSec > t) return undefined;
  }
  return undefined;
}

/**
 * Format remaining time as a gentle phrase, not a clock.
 * - More than 1 min: "X min remaining" (round up).
 * - 60–10 sec: "Less than a minute"
 * - <10 sec: "Almost done"
 */
export function formatRemaining(sec: number): string {
  if (sec <= 0) return 'Done';
  if (sec < 10) return 'Almost done';
  if (sec < 60) return 'Less than a minute';
  const min = Math.ceil(sec / 60);
  return `${min} min remaining`;
}
